import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { API, authHeaders } from "../lib/supabase";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", attempts: 0 });
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) return setState({ phase: "error", message: "Missing session_id" });
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await fetch(`${API}/billing/status/${sessionId}`, { headers: await authHeaders() });
        const data = await r.json();
        if (!r.ok) throw new Error(data.detail || "Status check failed");
        if (data.payment_status === "paid") return setState({ phase: "paid", data });
        if (data.payment_status === "expired" || data.payment_status === "failed") return setState({ phase: "error", message: `Payment ${data.payment_status}` });
        if (attempts >= 8) return setState({ phase: "error", message: "Payment still pending — check back shortly." });
        setState({ phase: "polling", attempts });
        timer.current = setTimeout(poll, 2000);
      } catch (e) { setState({ phase: "error", message: e.message }); }
    };
    poll();
    return () => clearTimeout(timer.current);
  }, [sessionId]);

  return (
    <main className="screen-center px-5">
      <section className="card w-full max-w-sm text-center fade-up" data-testid="billing-success-page">
        {state.phase === "polling" && (<>
          <Loader2 className="mx-auto animate-spin text-primary" size={40} />
          <h2 className="text-xl font-bold mt-4" data-testid="billing-polling">Confirming with Stripe…</h2>
          <p className="text-sm text-muted mt-1">Attempt {state.attempts + 1}</p>
        </>)}
        {state.phase === "paid" && (<>
          <CheckCircle2 className="mx-auto text-[#2ECC71]" size={44} />
          <h2 className="text-xl font-bold mt-4" data-testid="billing-paid">Payment confirmed</h2>
          {state.data.subscription_status === "active" ? (<>
            <p className="text-sm text-muted mt-1">Solo Pro · {state.data.pack_id}</p>
            <p className="wallet-number mt-3" data-testid="billing-new-balance">Unlimited*</p>
            <p className="text-muted text-sm">dispatches while your subscription is active</p>
          </>) : (<>
            <p className="text-sm text-muted mt-1">+{state.data.credits} credits · {state.data.pack_id}</p>
            <p className="wallet-number mt-3" data-testid="billing-new-balance">{state.data.credit_balance}</p>
            <p className="text-muted text-sm">credits now available</p>
          </>)}
        </>)}
        {state.phase === "error" && (<>
          <XCircle className="mx-auto text-[#FF7A7A]" size={44} />
          <h2 className="text-xl font-bold mt-4" data-testid="billing-error">{state.message}</h2>
        </>)}
        <Link to="/app" data-testid="billing-back-link" className="btn-primary inline-block mt-6">Back to dashboard</Link>
      </section>
    </main>
  );
}
